import { useEffect, useMemo, useState } from "react";
import { Label } from "./ui/label";
import { MultiSelect } from "./ui/multi-select";
import { api } from "../lib/api";

/** Fetches active zones, wards and mohallas once for territory filtering.
 *  Phase 183: `{ withSupervisors: true }` also loads the Supervisor filter options (`masters.supervisors`) — every option
 *  carries the ward ids it stands for, so the Zone → Supervisor → Ward → Mohalla cascade is computed client-side. */
export function useTerritoryMasters(code, { withSupervisors = false } = {}) {
  const [masters, setMasters] = useState({ zones: [], wards: [], mohallas: [], supervisors: null });
  useEffect(() => {
    Promise.all([
      api.get(`/t/${code}/masters/zones`, { params: { include_inactive: false } }),
      api.get(`/t/${code}/masters/wards`, { params: { include_inactive: false } }),
      api.get(`/t/${code}/masters/mohallas`, { params: { include_inactive: false } }),
      withSupervisors ? api.get(`/t/${code}/reports/field-activity/supervisors`).catch(() => ({ data: { options: [] } })) : Promise.resolve(null),
    ]).then(([z, w, m, s]) => setMasters({ zones: z.data, wards: w.data, mohallas: m.data, supervisors: s ? (s.data?.options || []) : null }))
      .catch(() => {});
  }, [code, withSupervisors]);
  return masters;
}

// ---------------------------------------------------------------------------------------------------------------
// Phase 176 — every level is MULTI-select. The filter value keeps its historical shape { zoneId, wardId, mohallaId }
// but each field is now a comma-joined list of ids ("" = all). The same strings travel to the API (`ward_id=a,b`),
// into URLs (dashboard drill-downs) and into exports, so every consumer that passed them through keeps working.
// Phase 183 — optional `supervisorId` (comma-joined option keys `sup:<id>` / `unassigned:<zone>`) sits between zone and
// ward; it travels as `supervisor_id` and narrows the ward / mohalla options to the selected supervisors' wards.
// ---------------------------------------------------------------------------------------------------------------
export const splitIds = (s) => (Array.isArray(s) ? s.filter(Boolean) : String(s || "").split(",").map((x) => x.trim()).filter(Boolean));
export const joinIds = (arr) => (arr || []).filter(Boolean).join(",");
export const EMPTY_TERRITORY = { zoneId: "", supervisorId: "", wardId: "", mohallaId: "" };

/** Supervisor options visible for a zone selection (all of them when no zone is chosen / locked zones otherwise). */
export function supervisorOptionsFor(masters, zoneIds, lockedZoneIds = null) {
  const all = masters?.supervisors || [];
  const locked = Array.isArray(lockedZoneIds) && lockedZoneIds.length > 0;
  const scope = splitIds(zoneIds).length ? splitIds(zoneIds) : (locked ? lockedZoneIds : null);
  if (!scope) return all;
  const z = new Set(scope);
  return all.filter((o) => (o.zone_ids || []).some((id) => z.has(id)));
}

/** Union of the wards the selected supervisors stand for (null when none selected). */
function supervisorWardSet(masters, supervisorIds) {
  const keys = splitIds(supervisorIds);
  if (!keys.length) return null;
  const byKey = new Map((masters?.supervisors || []).map((o) => [o.key, o]));
  const set = new Set();
  keys.forEach((k) => (byKey.get(k)?.ward_ids || []).forEach((w) => set.add(w)));
  return set;
}

/** Options visible at each level for the current selection (cascade: supervisors of the chosen zones, wards of the
 *  chosen zones ∩ the chosen supervisors' wards, mohallas of the chosen wards). */
export function territoryOptions(masters, value, lockedZoneIds = null) {
  const zoneIds = splitIds(value?.zoneId), wardIds = splitIds(value?.wardId);
  const locked = Array.isArray(lockedZoneIds) && lockedZoneIds.length > 0;
  const zones = locked ? masters.zones.filter((z) => lockedZoneIds.includes(z.id)) : masters.zones;
  const zoneScope = zoneIds.length ? zoneIds : (locked ? lockedZoneIds : null);
  const supervisors = supervisorOptionsFor(masters, zoneIds, lockedZoneIds);
  const supWards = supervisorWardSet(masters, value?.supervisorId);
  let wards = zoneScope ? masters.wards.filter((w) => zoneScope.includes(w.zone_id)) : masters.wards;
  if (supWards) wards = wards.filter((w) => supWards.has(w.id));
  const wardScope = wardIds.length ? wardIds : (zoneScope || supWards ? wards.map((w) => w.id) : null);
  const mohallas = wardScope ? masters.mohallas.filter((m) => wardScope.includes(m.ward_id)) : masters.mohallas;
  return { zones, supervisors, wards, mohallas };
}

/** Drop supervisor / ward / mohalla ids that fell outside the (new) parent selection. */
export function pruneTerritory(masters, value, lockedZoneIds = null) {
  const supOk = new Set(supervisorOptionsFor(masters, value?.zoneId, lockedZoneIds).map((o) => o.key));
  // an option list that has not loaded yet must not wipe a selection (keys are validated once it arrives)
  const supervisorIds = masters?.supervisors ? splitIds(value?.supervisorId).filter((k) => supOk.has(k)) : splitIds(value?.supervisorId);
  const v = { ...value, supervisorId: joinIds(supervisorIds) };
  const { wards, mohallas } = territoryOptions(masters, { ...v, wardId: "" }, lockedZoneIds);
  const wardOk = new Set(wards.map((w) => w.id));
  const wardIds = splitIds(v?.wardId).filter((id) => wardOk.has(id));
  const { mohallas: moh2 } = territoryOptions(masters, { ...v, wardId: joinIds(wardIds) }, lockedZoneIds);
  const mohOk = new Set((wardIds.length ? moh2 : mohallas).map((m) => m.id));
  const mohallaIds = splitIds(v?.mohallaId).filter((id) => mohOk.has(id));
  return { zoneId: joinIds(splitIds(v?.zoneId)), supervisorId: joinIds(supervisorIds), wardId: joinIds(wardIds), mohallaId: joinIds(mohallaIds) };
}

/** "Zone-1, Zone-2 › Rajesh Verma › Ward 1, Ward 5 › Supela" (names from the client-side masters; "" when nothing selected). */
export function territoryLabel(masters, value, { sep = " › ", max = 4 } = {}) {
  const part = (ids, list, key, fallback) => {
    const sel = splitIds(ids);
    if (!sel.length) return null;
    const names = sel.map((id) => list.find((x) => x.id === id)?.[key] || fallback);
    return names.length > max ? `${names.slice(0, max).join(", ")} +${names.length - max} more` : names.join(", ");
  };
  const sups = (masters?.supervisors || []).map((o) => ({ id: o.key, name: o.name }));
  return [part(value?.zoneId, masters.zones, "zone_name", "Zone"), part(value?.supervisorId, sups, "name", "Supervisor"),
    part(value?.wardId, masters.wards, "ward_name", "Ward"),
    part(value?.mohallaId, masters.mohallas, "mohalla_name", "Mohalla")].filter(Boolean).join(sep);
}

/** Query params for the API (only the levels that are set; values are comma-joined id lists). */
export function territoryParams(value) {
  const out = {};
  if (splitIds(value?.zoneId).length) out.zone_id = joinIds(splitIds(value.zoneId));
  if (splitIds(value?.supervisorId).length) out.supervisor_id = joinIds(splitIds(value.supervisorId));
  if (splitIds(value?.wardId).length) out.ward_id = joinIds(splitIds(value.wardId));
  if (splitIds(value?.mohallaId).length) out.mohalla_id = joinIds(splitIds(value.mohallaId));
  return out;
}

/** Phase 183: the ward ids a selection effectively covers when supervisors (but no wards) are chosen — lets pages that
 *  only understand zone / ward (e.g. the Collections register link) follow the Supervisor filter. "" when not needed. */
export function effectiveWardIds(masters, value, lockedZoneIds = null) {
  if (!splitIds(value?.supervisorId).length || splitIds(value?.wardId).length) return "";
  return joinIds(territoryOptions(masters, value, lockedZoneIds).wards.map((w) => w.id));
}

/**
 * Cascading Zone -> (Supervisor) -> Ward -> Mohalla multi-select filters.
 * value = { zoneId, supervisorId, wardId, mohallaId } (comma-joined ids, "" = All); onChange receives the updated object.
 * `lockedZoneIds` (Phase 143): a zone-mapped supervisor only ever sees their own zone(s) — one zone → fixed & disabled.
 * `levels` lets a page hide a level (e.g. ["zone", "ward"] when it has no mohalla filter) or add "supervisor" (Phase 183 —
 * needs `masters` loaded with `useTerritoryMasters(code, { withSupervisors: true })`).
 */
export function TerritoryFilterSelects({ masters, value, onChange, testIdPrefix = "territory", lockedZoneIds = null,
  levels = ["zone", "ward", "mohalla"], className = "", compact = false }) {
  const v = value || EMPTY_TERRITORY;
  const locked = Array.isArray(lockedZoneIds) && lockedZoneIds.length > 0;
  const singleLock = locked && lockedZoneIds.length === 1;
  const lockedZone = singleLock ? masters.zones.find((z) => z.id === lockedZoneIds[0]) : null;
  const { zones, supervisors, wards, mohallas } = useMemo(() => territoryOptions(masters, v, lockedZoneIds), [masters, v, lockedZoneIds]);
  const zoneName = (id) => masters.zones.find((z) => z.id === id)?.zone_name;
  const wardName = (id) => masters.wards.find((w) => w.id === id)?.ward_name;
  const supLoading = levels.includes("supervisor") && masters.supervisors == null;
  const nSup = splitIds(v.supervisorId).length;

  const update = (patch) => onChange(pruneTerritory(masters, { ...v, ...patch }, lockedZoneIds));
  const cols = levels.length;
  const h = compact ? "h-9" : "h-10";
  const grid = cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : cols === 3 ? "sm:grid-cols-3" : cols === 2 ? "sm:grid-cols-2" : "";

  return (
    <div className={`grid grid-cols-1 ${grid} gap-2 ${className}`} data-testid={`${testIdPrefix}-filters`}>
      {levels.includes("zone") && (
        <div className="space-y-1">
          <Label className="text-xs">Zone{splitIds(v.zoneId).length > 1 ? ` (${splitIds(v.zoneId).length})` : ""}</Label>
          <MultiSelect options={zones.map((z) => ({ value: z.id, label: z.zone_name }))}
            value={singleLock ? [lockedZoneIds[0]] : splitIds(v.zoneId)} onChange={(ids) => update({ zoneId: joinIds(ids) })}
            allLabel={locked ? "All my zones" : "All zones"} noun="zone" placeholder="Search zones…" className={h}
            disabled={singleLock} lockedLabel={singleLock ? (lockedZone?.zone_name || "My zone") : null}
            testId={`${testIdPrefix}-zone-select`} />
        </div>
      )}
      {levels.includes("supervisor") && (
        <div className="space-y-1" data-testid={`${testIdPrefix}-supervisor-level`}>
          <Label className="text-xs">Supervisor{nSup > 1 ? ` (${nSup})` : ""}</Label>
          <MultiSelect
            options={supervisors.map((o) => ({
              value: o.key,
              // "Unassigned (Zone Placeholder)" with the GESIL-<ZONE> name underneath; staff as "Name (EMP-001)"
              label: o.kind === "unassigned" ? "Unassigned (Zone Placeholder)" : (o.employee_id ? `${o.name} (${o.employee_id})` : o.name),
              hint: `${o.kind === "unassigned" && o.placeholder_name ? `${o.placeholder_name} · ` : ""}${o.ward_count} ward${o.ward_count === 1 ? "" : "s"}`,
              group: (o.zone_names || []).join(", ") || undefined,
            }))}
            value={splitIds(v.supervisorId)} onChange={(ids) => update({ supervisorId: joinIds(ids) })}
            allLabel={supLoading ? "Loading supervisors…" : "All supervisors"} noun="supervisor" placeholder="Search supervisors…"
            className={h} disabled={supLoading} emptyText={splitIds(v.zoneId).length ? "No supervisor is mapped to the selected zone(s)" : "No supervisors"}
            testId={`${testIdPrefix}-supervisor-select`} />
        </div>
      )}
      {levels.includes("ward") && (
        <div className="space-y-1">
          <Label className="text-xs">Ward{splitIds(v.wardId).length > 1 ? ` (${splitIds(v.wardId).length})` : ""}</Label>
          <MultiSelect options={wards.map((w) => ({ value: w.id, label: w.ward_name, group: !splitIds(v.zoneId).length || splitIds(v.zoneId).length > 1 ? zoneName(w.zone_id) : undefined }))}
            value={splitIds(v.wardId)} onChange={(ids) => update({ wardId: joinIds(ids) })}
            allLabel="All wards" noun="ward" placeholder="Search wards…" className={h} testId={`${testIdPrefix}-ward-select`} />
        </div>
      )}
      {levels.includes("mohalla") && (
        <div className="space-y-1">
          <Label className="text-xs">Mohalla{splitIds(v.mohallaId).length > 1 ? ` (${splitIds(v.mohallaId).length})` : ""}</Label>
          <MultiSelect options={mohallas.map((m) => ({ value: m.id, label: m.mohalla_name, group: splitIds(v.wardId).length !== 1 ? wardName(m.ward_id) : undefined }))}
            value={splitIds(v.mohallaId)} onChange={(ids) => update({ mohallaId: joinIds(ids) })}
            allLabel="All mohallas" noun="mohalla" placeholder="Search mohallas…" className={h} testId={`${testIdPrefix}-mohalla-select`} />
        </div>
      )}
    </div>
  );
}

/** Client-side territory match helper for docs carrying zone_id / ward_id / mohalla_id (lists: any id matches). */
export function matchesTerritory(doc, { zoneId, wardId, mohallaId }) {
  const z = splitIds(zoneId), w = splitIds(wardId), m = splitIds(mohallaId);
  if (z.length && !z.includes(doc.zone_id)) return false;
  if (w.length && !w.includes(doc.ward_id)) return false;
  if (m.length && !m.includes(doc.mohalla_id)) return false;
  return true;
}

/** "Ward 1, Ward 5" for a comma-joined id list against a master list ("" when empty). */
export function namesOf(ids, list, key, fallback = "") {
  const sel = splitIds(ids);
  if (!sel.length) return "";
  return sel.map((id) => (list || []).find((x) => x.id === id)?.[key] || fallback || id).join(", ");
}
